import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { impersonateUser } from "../services/api";
import toast from "react-hot-toast";

/**
 * Tiny per-row CTA that lets the platform admin borrow another user's
 * session for a debug session. The heavy lifting lives in
 * AuthContext.startImpersonation; this component only handles the
 * spinner + the toast on failure.
 *
 * Hidden when the caller isn't a platform_admin or when the target is
 * the current user themself (which the backend also refuses).
 */
export default function LoginAsButton({
  userId,
  className = "",
  label = "Login as",
  compact = false,
}: {
  userId: string;
  className?: string;
  label?: string;
  compact?: boolean;
}) {
  const { user, roles, startImpersonation, impersonator } = useAuth();
  const [loading, setLoading] = useState(false);

  // Only platform admins get to see this. Hide on their own row too —
  // the backend rejects self-impersonation but we shouldn't even tempt
  // them with the button.
  const isPlatformAdmin = roles.some(
    (r) => r.code === "platform_admin" && r.scope_type === "platform"
  );
  // If we're already in an impersonation session, don't allow nesting —
  // the platform_admin must exit first or things get confusing.
  if (!isPlatformAdmin || impersonator || user?.id === userId) return null;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const { token } = await impersonateUser(userId);
      await startImpersonation(token);
      // `startImpersonation` does a full reload, so we never reach this
      // line on success.
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Nu s-a putut iniția sesiunea de debug");
      setLoading(false);
    }
  };

  const baseClass = compact
    ? "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200"
    : "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 transition-colors";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      className={`${baseClass} disabled:opacity-50 ${className}`}
      title="Conectează-te ca acest utilizator pentru debugging"
    >
      {loading ? (
        <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      )}
      {label}
    </button>
  );
}
